import React, { useEffect, useState } from 'react';
import { X, RefreshCw, Trophy, ExternalLink, Loader, TrendingDown, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import apiClient from '../services/api/client';
import { Product } from '../services/productService';

// Resposta de GET /mercado/concorrentes/<id> (analisador_mercado.concorrentes no Python)
type Oferta = {
  marketplace: string; vendedor: string; preco: number | null; link?: string; titulo: string;
  exato?: boolean; origem?: 'catalogo' | 'ean' | 'titulo' | 'nome' | 'categoria' | 'referencia'; similaridade?: number;
  frete?: number; total?: number; frete_gratis?: boolean; full?: boolean; fba?: boolean;
  buybox?: boolean; vendidos?: number;
};
type Resumo = {
  vendedores: number; qtd: number; min: number | null; media: number | null; mediana: number | null;
  max: number | null; meu_preco: number | null; exatos: number; parecidos: number;
  posicao?: number; dif_menor_pct?: number;
};
type Canal = {
  status: 'ok' | 'erro' | 'bloqueado' | 'indisponivel'; motivo: string; fonte: string;
  ofertas: Oferta[]; exato?: boolean; buybox?: { preco: number; meu: boolean } | null; resumo: Resumo;
};
type Resposta = {
  sku: string; titulo: string; ean: string | null; geradoEm: string; duracaoSeg: number; cache: boolean;
  canais: Record<string, Canal>;
};

const NOMES: Record<string, string> = { ml: 'Mercado Livre', amazon: 'Amazon', shopee: 'Shopee', magalu: 'Magazine Luiza' };
const CORES: Record<string, string> = { ml: '#FFE600', amazon: '#FF9900', shopee: '#EE4D2D', magalu: '#0086FF' };
const ORIGEM: Record<string, string> = { catalogo: 'catálogo', ean: 'EAN', titulo: 'catálogo (título)', nome: 'busca por nome', categoria: 'mais vendidos da categoria', referencia: 'referência da categoria' };

const brl = (v?: number | null) =>
  v == null ? '—' : `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ConcorrentesModal({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState('ml');
  const [soExatos, setSoExatos] = useState(false);

  const carregar = async (refresh = false) => {
    if (!product) return;
    setLoading(true); setErro('');
    try {
      const id = (product as any)._id || product.id;
      const r = await apiClient.get(`/mercado/concorrentes/${id}`, {
        params: { limit: 20, ...(refresh ? { refresh: 1 } : {}) }, timeout: 90000,
      });
      setDados(r.data);
      // abre na primeira aba que tem ofertas
      const primeira = Object.entries(r.data.canais as Record<string, Canal>).find(([, c]) => c.ofertas?.length)?.[0];
      if (primeira) setAba(primeira);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Não foi possível buscar os concorrentes.');
    } finally { setLoading(false); }
  };

  useEffect(() => { setDados(null); if (product) carregar(); /* eslint-disable-next-line */ }, [product]);

  if (!product) return null;
  const canal = dados?.canais?.[aba];
  const ofertas = (canal?.ofertas || []).filter(o => !soExatos || o.exato);
  const r = canal?.resumo;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-dark-900 border border-dark-700 rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-dark-700">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-dark-100">Concorrentes</h2>
            <p className="text-sm text-dark-300 truncate">{product.title}</p>
            <p className="text-xs text-dark-500 mt-0.5">
              SKU {product.sku} · Mercado Livre: catálogo + anúncios parecidos (busca por nome ou mais vendidos da categoria)
              {dados && <> · {dados.cache ? 'resultado de até 6h atrás' : `atualizado agora (${dados.duracaoSeg}s)`}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => carregar(true)} disabled={loading}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 text-dark-200 text-sm disabled:opacity-50">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Atualizar
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-dark-800 text-dark-400"><X size={20} /></button>
          </div>
        </div>

        {/* Abas */}
        {dados && (
          <div className="flex gap-1 px-5 pt-3 border-b border-dark-700 overflow-x-auto">
            {Object.entries(dados.canais).map(([id, c]) => (
              <button key={id} onClick={() => setAba(id)}
                      className={clsx('px-4 py-2 text-sm rounded-t-lg border-b-2 whitespace-nowrap',
                        aba === id ? 'text-dark-100 bg-dark-800' : 'text-dark-400 border-transparent hover:text-dark-200')}
                      style={aba === id ? { borderColor: CORES[id] } : undefined}>
                {NOMES[id] || id}
                <span className={clsx('ml-2 text-xs px-1.5 rounded',
                  c.status !== 'ok' ? 'bg-red-500/10 text-red-400' : c.ofertas.length ? 'bg-dark-700 text-dark-200' : 'bg-dark-700 text-dark-500')}>
                  {c.status !== 'ok' ? '!' : c.ofertas.length}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="p-5 overflow-y-auto">
          {loading && !dados && (
            <div className="flex items-center justify-center gap-3 py-16 text-dark-400">
              <Loader className="animate-spin" size={20} /> Buscando o mesmo produto em outros vendedores… (pode levar até 1 minuto)
            </div>
          )}
          {erro && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm">{erro}</div>}

          {canal && (
            <>
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Card label="Seu preço" valor={brl(r?.meu_preco)} />
                <Card label="Menor preço" valor={brl(r?.min)} destaque={r?.min != null && r?.meu_preco != null && r.min < r.meu_preco ? 'red' : 'green'} />
                <Card label="Mediana" valor={brl(r?.mediana)} />
                <Card label="Concorrentes" valor={`${r?.vendedores ?? 0}`} sub={`${r?.exatos ?? 0} exatos · ${r?.parecidos ?? 0} parecidos`} />
                <Card label="Sua posição"
                      valor={r?.posicao ? `${r.posicao}º de ${(r.qtd || 0) + 1}` : '—'}
                      sub={r?.dif_menor_pct != null ? `${r.dif_menor_pct > 0 ? '+' : ''}${r.dif_menor_pct}% vs. menor` : ''}
                      destaque={r?.posicao === 1 ? 'green' : r?.posicao ? 'red' : undefined} />
              </div>

              {canal.buybox && (
                <div className={clsx('flex items-center gap-2 text-sm mb-4 px-3 py-2 rounded-lg border',
                  canal.buybox.meu ? 'text-emerald-300 bg-emerald-500/5 border-emerald-500/20' : 'text-yellow-300 bg-yellow-500/5 border-yellow-500/20')}>
                  <Trophy size={16} />
                  {canal.buybox.meu
                    ? 'Você está ganhando a disputa (Buy Box / catálogo).'
                    : `Quem está ganhando a disputa vende por ${brl(canal.buybox.preco)}.`}
                </div>
              )}

              {canal.status !== 'ok' || canal.motivo ? (
                <p className={clsx('text-sm mb-3', canal.status === 'ok' ? 'text-dark-400' : 'text-red-400')}>
                  {canal.status === 'indisponivel' ? 'Indisponível: ' : canal.status === 'bloqueado' ? 'Bloqueado: ' : ''}{canal.motivo}
                </p>
              ) : null}

              {canal.ofertas.length > 0 && (
                <label className="flex items-center gap-2 text-sm text-dark-300 mb-3 cursor-pointer w-fit">
                  <input type="checkbox" checked={soExatos} onChange={e => setSoExatos(e.target.checked)} />
                  Mostrar só o mesmo produto (EAN/catálogo)
                </label>
              )}

              {/* Tabela */}
              {ofertas.length > 0 && ofertas.every(o => o.origem === 'referencia') && (
                <div className="mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-xs text-amber-900">
                  Nenhum anúncio com o <strong>mesmo produto</strong> foi encontrado no Mercado Livre (comum em moda/produção
                  própria). Abaixo estão os <strong>mais vendidos da categoria</strong>, como <strong>referência de preço</strong> —
                  não são o mesmo produto.
                </div>
              )}
              {ofertas.length > 0 && (
                <div className="border border-dark-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-dark-800 text-dark-400 text-xs uppercase">
                      <tr>
                        <th className="text-left p-3">Vendedor</th>
                        <th className="text-left p-3">Anúncio</th>
                        <th className="text-right p-3">Preço</th>
                        <th className="text-right p-3">vs. você</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ofertas.map((o, i) => {
                        const preco = o.total ?? o.preco;
                        const dif = preco != null && r?.meu_preco ? ((r.meu_preco - preco) / preco) * 100 : null;
                        return (
                          <tr key={i} className="border-t border-dark-800 hover:bg-dark-800/40">
                            <td className="p-3 text-dark-200 align-top">
                              <div className="font-medium flex items-center gap-1">
                                {o.buybox && <Trophy size={14} className="text-yellow-400" />}{o.vendedor}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                <Tag cor={o.exato ? 'green' : 'gray'}>{o.exato ? 'mesmo produto' : `parecido ${Math.round((o.similaridade || 0) * 100)}%`}</Tag>
                                {o.origem && <Tag cor="gray">via {ORIGEM[o.origem] || o.origem}</Tag>}
                                {(o.full || o.fba) && <Tag cor="blue">{o.fba ? 'FBA' : 'Full'}</Tag>}
                                {o.frete_gratis && <Tag cor="blue">frete grátis</Tag>}
                              </div>
                            </td>
                            <td className="p-3 text-dark-300 align-top">
                              <div className="line-clamp-2">{o.titulo}</div>
                              {o.vendidos != null && <div className="text-xs text-dark-500 mt-0.5">{o.vendidos} vendidos</div>}
                            </td>
                            <td className="p-3 text-right text-dark-100 font-semibold align-top whitespace-nowrap">
                              {brl(preco)}
                              {o.frete ? <div className="text-xs text-dark-500 font-normal">{brl(o.preco)} + {brl(o.frete)} frete</div> : null}
                            </td>
                            <td className="p-3 text-right align-top whitespace-nowrap">
                              {dif == null ? '—' : (
                                <span className={clsx('inline-flex items-center gap-1', dif > 0 ? 'text-red-400' : 'text-emerald-400')}>
                                  {dif > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                  {dif > 0 ? '+' : ''}{dif.toFixed(1)}%
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right align-top">
                              {o.link && (
                                <a href={o.link} target="_blank" rel="noopener noreferrer" title="Abrir anúncio"
                                   className="inline-flex p-1.5 rounded hover:bg-dark-700 text-dark-400 hover:text-dark-100">
                                  <ExternalLink size={16} />
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {canal.status === 'ok' && ofertas.length === 0 && !canal.motivo && (
                <p className="text-sm text-dark-400">Nenhum concorrente encontrado neste marketplace.</p>
              )}
              <p className="text-xs text-dark-500 mt-3">
                "vs. você": quanto o seu preço está acima (vermelho) ou abaixo (verde) do concorrente. Seus próprios anúncios não aparecem.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ label, valor, sub, destaque }: { label: string; valor: string; sub?: string; destaque?: 'green' | 'red' }) {
  return (
    <div className="bg-dark-800 border border-dark-700 rounded-lg p-3">
      <p className="text-xs text-dark-500">{label}</p>
      <p className={clsx('text-lg font-bold', destaque === 'green' ? 'text-emerald-400' : destaque === 'red' ? 'text-red-400' : 'text-dark-100')}>{valor}</p>
      {sub && <p className="text-xs text-dark-500">{sub}</p>}
    </div>
  );
}

function Tag({ children, cor }: { children: React.ReactNode; cor: 'green' | 'gray' | 'blue' }) {
  return (
    <span className={clsx('text-[10px] px-1.5 py-0.5 rounded',
      cor === 'green' ? 'bg-emerald-500/10 text-emerald-400' : cor === 'blue' ? 'bg-sky-500/10 text-sky-400' : 'bg-dark-700 text-dark-400')}>
      {children}
    </span>
  );
}
